import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { sendRoutedPrintJob } from './printerRoutingService';
import { RoutedPrintPayload, PrinterStatusUpdate } from '../types/printerRouting';

export type RoutedPrintJobStatus = 'pending' | 'processing' | 'completed' | 'partial' | 'failed' | 'retrying';

export interface PrinterJobStatus {
  printerId: string;
  printerName: string;
  status: 'pending' | 'success' | 'failed' | 'retrying';
  attempts: number;
  maxAttempts: number;
  lastAttempt?: number;
  errorMessage?: string;
}

export interface RoutedPrintJob {
  id: string;
  orderId: string;
  payload: RoutedPrintPayload;
  status: RoutedPrintJobStatus;
  printerStatuses: PrinterJobStatus[];
  createdAt: number;
  completedAt?: number;
  lastProcessedAt?: number;
}

export interface RoutedPrintLog {
  id: string;
  jobId: string;
  orderId: string;
  printerId: string;
  timestamp: number;
  status: 'success' | 'error' | 'retry';
  message: string;
  attemptNumber: number;
  errorDetails?: string;
}

interface RoutedPrintQueueDB extends DBSchema {
  routedPrintQueue: {
    key: string;
    value: RoutedPrintJob;
    indexes: { 'by-status': RoutedPrintJobStatus; 'by-order': string; 'by-created': number };
  };
  routedPrintLogs: {
    key: string;
    value: RoutedPrintLog;
    indexes: { 'by-job': string; 'by-order': string; 'by-printer': string; 'by-timestamp': number };
  };
}

const DB_NAME = 'routed-print-queue-db';
const DB_VERSION = 1;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 3000;
const PROCESS_INTERVAL_MS = 5000;

let dbInstance: IDBPDatabase<RoutedPrintQueueDB> | null = null;
let processingInterval: NodeJS.Timeout | null = null;

const initDB = async (): Promise<IDBPDatabase<RoutedPrintQueueDB>> => {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<RoutedPrintQueueDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('routedPrintQueue')) {
        const queueStore = db.createObjectStore('routedPrintQueue', { keyPath: 'id' });
        queueStore.createIndex('by-status', 'status');
        queueStore.createIndex('by-order', 'orderId');
        queueStore.createIndex('by-created', 'createdAt');
      }

      if (!db.objectStoreNames.contains('routedPrintLogs')) {
        const logsStore = db.createObjectStore('routedPrintLogs', { keyPath: 'id' });
        logsStore.createIndex('by-job', 'jobId');
        logsStore.createIndex('by-order', 'orderId');
        logsStore.createIndex('by-printer', 'printerId');
        logsStore.createIndex('by-timestamp', 'timestamp');
      }
    },
  });

  return dbInstance;
};

const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const log = async (
  jobId: string,
  orderId: string,
  printerId: string,
  status: 'success' | 'error' | 'retry',
  message: string,
  attemptNumber: number,
  errorDetails?: string
): Promise<void> => {
  try {
    const db = await initDB();
    const logEntry: RoutedPrintLog = {
      id: generateId(),
      jobId,
      orderId,
      printerId,
      timestamp: Date.now(),
      status,
      message,
      attemptNumber,
      errorDetails,
    };
    await db.add('routedPrintLogs', logEntry);
  } catch (error) {
    console.error('[RoutedPrintQueue] Erreur lors du logging:', error);
  }
};

export const addRoutedPrintJob = async (
  orderId: string,
  payload: RoutedPrintPayload
): Promise<string> => {
  const db = await initDB();

  const printerStatuses: PrinterJobStatus[] = payload.tickets.map(ticket => ({
    printerId: ticket.printerId,
    printerName: ticket.printerName,
    status: 'pending',
    attempts: 0,
    maxAttempts: MAX_RETRY_ATTEMPTS,
  }));

  const job: RoutedPrintJob = {
    id: generateId(),
    orderId,
    payload,
    status: 'pending',
    printerStatuses,
    createdAt: Date.now(),
  };

  await db.add('routedPrintQueue', job);
  console.log(`[RoutedPrintQueue] Job ajouté: ${job.id} pour commande ${orderId}`);

  startProcessing();

  return job.id;
};

const processJob = async (job: RoutedPrintJob): Promise<void> => {
  const db = await initDB();

  job.status = 'processing';
  job.lastProcessedAt = Date.now();
  await db.put('routedPrintQueue', job);

  const pendingPrinters = job.printerStatuses.filter(
    ps => ps.status === 'pending' || ps.status === 'retrying'
  );

  if (pendingPrinters.length === 0) {
    job.status = 'completed';
    job.completedAt = Date.now();
    await db.put('routedPrintQueue', job);
    return;
  }

  const ticketsToPrint = job.payload.tickets.filter(ticket =>
    pendingPrinters.some(ps => ps.printerId === ticket.printerId)
  );

  const payloadToSend: RoutedPrintPayload = {
    ...job.payload,
    tickets: ticketsToPrint,
  };

  try {
    const results: PrinterStatusUpdate[] = await sendRoutedPrintJob(payloadToSend);

    for (const result of results) {
      const printerStatus = job.printerStatuses.find(ps => ps.printerId === result.printerId);
      if (!printerStatus) continue;

      printerStatus.attempts++;
      printerStatus.lastAttempt = Date.now();

      if (result.success) {
        printerStatus.status = 'success';
        await log(
          job.id,
          job.orderId,
          result.printerId,
          'success',
          `Impression réussie sur ${printerStatus.printerName}`,
          printerStatus.attempts
        );
      } else {
        if (printerStatus.attempts >= printerStatus.maxAttempts) {
          printerStatus.status = 'failed';
          printerStatus.errorMessage = result.error || 'Échec après plusieurs tentatives';
          await log(
            job.id,
            job.orderId,
            result.printerId,
            'error',
            `Échec définitif sur ${printerStatus.printerName}`,
            printerStatus.attempts,
            result.error
          );
        } else {
          printerStatus.status = 'retrying';
          printerStatus.errorMessage = result.error;
          await log(
            job.id,
            job.orderId,
            result.printerId,
            'retry',
            `Tentative ${printerStatus.attempts}/${printerStatus.maxAttempts} échouée, nouvelle tentative prévue`,
            printerStatus.attempts,
            result.error
          );
        }
      }
    }

    const allSuccess = job.printerStatuses.every(ps => ps.status === 'success');
    const anySuccess = job.printerStatuses.some(ps => ps.status === 'success');
    const allFailed = job.printerStatuses.every(ps => ps.status === 'failed');

    if (allSuccess) {
      job.status = 'completed';
      job.completedAt = Date.now();
    } else if (allFailed) {
      job.status = 'failed';
      job.completedAt = Date.now();
    } else if (anySuccess) {
      job.status = 'partial';
    } else {
      job.status = 'retrying';
    }

    await db.put('routedPrintQueue', job);
  } catch (error) {
    console.error('[RoutedPrintQueue] Erreur lors du traitement du job:', error);
    job.status = 'retrying';
    await db.put('routedPrintQueue', job);

    for (const printerStatus of pendingPrinters) {
      await log(
        job.id,
        job.orderId,
        printerStatus.printerId,
        'error',
        'Erreur lors de la communication avec Android',
        printerStatus.attempts + 1,
        String(error)
      );
    }
  }
};

const processQueue = async (): Promise<void> => {
  try {
    const db = await initDB();
    const pendingJobs = await db.getAllFromIndex('routedPrintQueue', 'by-status', 'pending');
    const retryingJobs = await db.getAllFromIndex('routedPrintQueue', 'by-status', 'retrying');

    const jobsToProcess = [...pendingJobs, ...retryingJobs].filter(job => {
      if (!job.lastProcessedAt) return true;
      return Date.now() - job.lastProcessedAt >= RETRY_DELAY_MS;
    });

    for (const job of jobsToProcess) {
      await processJob(job);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  } catch (error) {
    console.error('[RoutedPrintQueue] Erreur lors du traitement de la file:', error);
  }
};

export const startProcessing = (): void => {
  if (processingInterval) return;

  console.log('[RoutedPrintQueue] Démarrage du traitement de la file');
  processingInterval = setInterval(processQueue, PROCESS_INTERVAL_MS);
  processQueue();
};

export const stopProcessing = (): void => {
  if (processingInterval) {
    clearInterval(processingInterval);
    processingInterval = null;
    console.log('[RoutedPrintQueue] Arrêt du traitement de la file');
  }
};

export const getQueueStats = async () => {
  const db = await initDB();
  const allJobs = await db.getAll('routedPrintQueue');

  return {
    total: allJobs.length,
    pending: allJobs.filter(j => j.status === 'pending').length,
    processing: allJobs.filter(j => j.status === 'processing').length,
    completed: allJobs.filter(j => j.status === 'completed').length,
    partial: allJobs.filter(j => j.status === 'partial').length,
    failed: allJobs.filter(j => j.status === 'failed').length,
    retrying: allJobs.filter(j => j.status === 'retrying').length,
  };
};

export const getJobsByOrder = async (orderId: string): Promise<RoutedPrintJob[]> => {
  const db = await initDB();
  return await db.getAllFromIndex('routedPrintQueue', 'by-order', orderId);
};

export const getJobById = async (jobId: string): Promise<RoutedPrintJob | undefined> => {
  const db = await initDB();
  return await db.get('routedPrintQueue', jobId);
};

export const getLogsByJob = async (jobId: string): Promise<RoutedPrintLog[]> => {
  const db = await initDB();
  return await db.getAllFromIndex('routedPrintLogs', 'by-job', jobId);
};

export const clearOldLogs = async (daysToKeep: number = 7): Promise<void> => {
  const db = await initDB();
  const cutoffTime = Date.now() - daysToKeep * 24 * 60 * 60 * 1000;

  const allLogs = await db.getAll('routedPrintLogs');
  const logsToDelete = allLogs.filter(log => log.timestamp < cutoffTime);

  for (const log of logsToDelete) {
    await db.delete('routedPrintLogs', log.id);
  }

  console.log(`[RoutedPrintQueue] ${logsToDelete.length} anciens logs supprimés`);
};

export const clearCompletedJobs = async (daysToKeep: number = 1): Promise<void> => {
  const db = await initDB();
  const cutoffTime = Date.now() - daysToKeep * 24 * 60 * 60 * 1000;

  const completedJobs = await db.getAllFromIndex('routedPrintQueue', 'by-status', 'completed');
  const jobsToDelete = completedJobs.filter(
    job => job.completedAt && job.completedAt < cutoffTime
  );

  for (const job of jobsToDelete) {
    await db.delete('routedPrintQueue', job.id);
  }

  console.log(`[RoutedPrintQueue] ${jobsToDelete.length} jobs complétés supprimés`);
};

export const retryFailedJob = async (jobId: string): Promise<void> => {
  const db = await initDB();
  const job = await db.get('routedPrintQueue', jobId);

  if (!job) {
    throw new Error('Job introuvable');
  }

  for (const printerStatus of job.printerStatuses) {
    if (printerStatus.status === 'failed') {
      printerStatus.status = 'retrying';
      printerStatus.attempts = 0;
      printerStatus.errorMessage = undefined;
    }
  }

  job.status = 'retrying';
  await db.put('routedPrintQueue', job);

  startProcessing();
};
